import type {
  ReviewRequestsData,
  ReviewState,
  Reviewer,
  CompletedReview,
  PendingReviewRequest,
} from '../../../page-data/payloads/review-requests'
import {reviewState} from './review-state-helpers'

export interface TeamCompositeState {
  teamName: string
  teamReviewer: Reviewer | null
  compositeState: ReviewState
  stateLabel: string
  completedUserReviews: CompletedReview[]
  pendingUserRequests: PendingReviewRequest[]
}

/**
 * Groups review activity by team and computes a single composite review state for each.
 *
 * Processes two data sources:
 * 1. Pending review requests — either directly assigned to a team, or delegated from a team to individual users.
 * 2. Completed reviews — submitted by users on behalf of teams.
 *
 * The composite state reflects the most significant review across all team members
 * (changes_requested > approved > commented > pending).
 */
export function computeTeamCompositeStates(data: ReviewRequestsData): TeamCompositeState[] {
  const teamReviewerMap = new Map<string, Reviewer>()
  const teamStates = new Map<string, ReviewState[]>()
  const teamCompletedReviews = new Map<string, CompletedReview[]>()
  const teamPendingRequests = new Map<string, PendingReviewRequest[]>()

  for (const req of data.pendingReviewRequests ?? []) {
    if (req.reviewer.reviewerType === 'Team') {
      // Team itself is directly requested — mark team state as pending.
      // No entry in teamPendingRequests because there is no delegated user yet.
      teamReviewerMap.set(req.reviewer.displayLogin, req.reviewer)
      const states = teamStates.get(req.reviewer.displayLogin) ?? []
      states.push('pending')
      teamStates.set(req.reviewer.displayLogin, states)
    } else if (req.delegatedFromTeam) {
      // An individual user was delegated from a team — track as a pending user request
      // so the UI can display them nested under the team row.
      const teamName = req.delegatedFromTeam.displayLogin
      teamReviewerMap.set(teamName, req.delegatedFromTeam)
      const pending = teamPendingRequests.get(teamName) ?? []
      pending.push(req)
      teamPendingRequests.set(teamName, pending)
    }
  }

  // Collect completed reviews and group them by the team(s) the reviewer acted on behalf of.
  for (const review of data.completedReviews ?? []) {
    for (const team of review.onBehalfOfTeams) {
      const teamName = team.displayLogin
      teamReviewerMap.set(teamName, team)
      const states = teamStates.get(teamName) ?? []
      states.push(review.state)
      teamStates.set(teamName, states)

      const reviews = teamCompletedReviews.get(teamName) ?? []
      reviews.push(review)
      teamCompletedReviews.set(teamName, reviews)
    }
  }

  const results: TeamCompositeState[] = []
  for (const [teamName, states] of teamStates) {
    const compositeState = resolveCompositeState(states)
    const {label} = reviewState(compositeState)
    results.push({
      teamName,
      teamReviewer: teamReviewerMap.get(teamName) ?? null,
      compositeState,
      stateLabel: label,
      completedUserReviews: teamCompletedReviews.get(teamName) ?? [],
      pendingUserRequests: teamPendingRequests.get(teamName) ?? [],
    })
  }

  return results
}

function resolveCompositeState(states: ReviewState[]): ReviewState {
  if (states.length === 0) return 'pending'

  // If anyone requested changes, team is blocked
  if (states.includes('changes_requested')) return 'changes_requested'
  // If anyone approved, team is approved (even if others only commented)
  if (states.includes('approved')) return 'approved'
  // If only comments, show commented
  if (states.includes('commented')) return 'commented'
  // Otherwise pending
  return 'pending'
}
